import { api } from '../lib/api'
import type { MatchResult } from '../types/api'

type SourcingAgentStatus = 'idle' | 'sourcing' | 'scoring' | 'generating' | 'complete' | 'error'

type SourcingAgentConfig = {
  jobId: string
  maxCandidates?: number
  minScore?: number
  autoOutreach?: boolean
}

type SourcedCandidate = {
  profileId: string
  name: string
  score: number
  reasons: string[]
  matchStrength: 'strong' | 'moderate' | 'weak'
}

type SourcingResult = {
  jobId: string
  status: SourcingAgentStatus
  sourced: SourcedCandidate[]
  timestamp: string
  error?: string
}

class SourcingAgent {
  private config: SourcingAgentConfig
  private status: SourcingAgentStatus = 'idle'
  private abortController: AbortController | null = null

  constructor(config: SourcingAgentConfig) {
    this.config = config
  }

  async start(): Promise<SourcingResult> {
    if (this.status === 'sourcing') {
      throw new Error('Agent is already running')
    }

    this.abortController = new AbortController()
    this.status = 'sourcing'

    try {
      this.status = 'scoring'

      // Step 1: Create match report for the job (gets all candidates scored)
      const matchRes = await api.jobs.matches.create(
        this.config.jobId,
        this.config.maxCandidates || 50
      )

      // Step 2: Filter and sort candidates by score
      const allMatches = matchRes.data.matches || []

      const filtered = allMatches
        .filter((match: MatchResult) =>
          match.score >= (this.config.minScore || 50)
        )
        .sort((a: MatchResult, b: MatchResult) => b.score - a.score)
        .slice(0, this.config.maxCandidates || 10)
        .map((match: MatchResult) => ({
          profileId: match.id,
          name: match.name,
          score: match.score,
          reasons: match.reasons || [],
          matchStrength: this.getMatchStrength(match.score)
        }))

      this.status = 'generating'

      // Step 3: Generate outreach messages if enabled
      if (this.config.autoOutreach) {
        for (const candidate of filtered) {
          // This would integrate with your messaging system
          console.log(`Generating outreach for ${candidate.name}`)
        }
      }

      this.status = 'complete'

      return {
        jobId: this.config.jobId,
        status: this.status,
        sourced: filtered,
        timestamp: new Date().toISOString()
      }

    } catch (err) {
      this.status = 'error'
      return {
        jobId: this.config.jobId,
        status: this.status,
        sourced: [],
        timestamp: new Date().toISOString(),
        error: err instanceof Error ? err.message : 'Unknown error'
      }
    }
  }

  stop() {
    this.abortController?.abort()
    this.status = 'idle'
  }

  getStatus(): SourcingAgentStatus {
    return this.status
  }

  private getMatchStrength(score: number): 'strong' | 'moderate' | 'weak' {
    if (score >= 80) return 'strong'
    if (score >= 60) return 'moderate'
    return 'weak'
  }
}

export default SourcingAgent
export type { SourcingAgentConfig, SourcedCandidate, SourcingResult, SourcingAgentStatus }
